import { useState, useCallback, useEffect, useRef } from "react";
import * as api from "../api";
import { obtenerUbicacion, precalentarUbicacion } from "../utils";

// Estado de fichaje del usuario contra Odoo.
//
// El backend guarda la sesión de Odoo de cada empleado (7 días), así que el
// widget solo pide credenciales la primera vez y cuando caduca. Cuando el
// backend responde 409/SESION o SIN_CONEXION, `conectado` pasa a false y el
// widget muestra el formulario.
// Marca de que esta pestaña ya contrasto sus fichajes con Odoo.
const CLAVE_SINC = "alm_fichajes_sincronizados";

export function useFichaje(token, showToast) {
  const [estado,    setEstado]    = useState(null);   // 'dentro' | 'fuera' | null
  const [conectado, setConectado] = useState(null);   // null = aún no se sabe
  const [desde,     setDesde]     = useState(null);   // ISO del tramo abierto
  const [hoy,       setHoy]       = useState([]);
  const [horasHoy,  setHorasHoy]  = useState(0);
  const [cargando,  setCargando]  = useState(false);
  const [fichando,  setFichando]  = useState(false);

  // Evita que dos peticiones en vuelo se pisen al escribir el estado
  const enVuelo = useRef(false);

  const aplicar = (d) => {
    setConectado(!!d.conectado);
    if (d.estado)      setEstado(d.estado);
    if ("desde" in d)  setDesde(d.desde || null);
    if (d.hoy)         setHoy(d.hoy);
    if ("horas_hoy" in d) setHorasHoy(d.horas_hoy || 0);
  };

  const cargarEstado = useCallback(async () => {
    if (!token || enVuelo.current) return;
    enVuelo.current = true;
    setCargando(true);
    try {
      aplicar(await api.getFichajeEstado());
    } catch (e) {
      // SIN_CONEXION y SESION no son errores que haya que gritarle al usuario:
      // simplemente hay que volver a conectar.
      if (/caducado|conectado/i.test(e.message || "")) setConectado(false);
      else showToast("No se pudo consultar el estado de fichaje", "error");
    } finally {
      enVuelo.current = false;
      setCargando(false);
    }
  }, [token]); // eslint-disable-line

  const conectar = async (login, password) => {
    try {
      await api.conectarOdoo(login, password);
      showToast("Cuenta de Odoo conectada");
      await cargarEstado();
      return true;
    } catch (e) {
      showToast(e.message || "No se pudo conectar con Odoo", "error");
      return false;
    }
  };

  const desconectar = async () => {
    try {
      await api.desconectarOdoo();
      setConectado(false); setEstado(null); setDesde(null); setHoy([]); setHorasHoy(0);
      showToast("Cuenta de Odoo desconectada", "warn");
    } catch (e) { showToast(e.message || "Error al desconectar", "error"); }
  };

  const fichar = async () => {
    if (fichando || !estado) return;
    const accion = estado === "dentro" ? "salida" : "entrada";
    setFichando(true);
    try {
      // Sin coordenadas, Odoo geolocaliza por la IP del servidor de ALM y
      // registra todos los fichajes en el centro de datos. Si el empleado
      // deniega el permiso, se ficha igual: `geo` va a null y Odoo usa su
      // respaldo.
      const geo = await obtenerUbicacion();
      const r = await api.fichar(accion, geo);
      aplicar({ conectado: true, ...r });
      if (r.pendiente) showToast("Fichaje registrado en ALM. Odoo no responde; se sincronizará.", "warn");
      // Que falte la ubicacion no es un fallo, pero conviene decirlo: si no, se
      // descubre dias despues mirando el registro de asistencias.
      else if (!geo) showToast(`${accion === "entrada" ? "Entrada" : "Salida"} registrada, sin ubicación`, "warn");
      // Un punto con kilometros de margen no localiza a nadie: casi siempre es
      // un equipo sin GPS, que se situa por wifi o por IP. Mejor decirlo en el
      // momento que descubrirlo semanas despues en el registro.
      else if (geo.precision > 1000)
        showToast(`${accion === "entrada" ? "Entrada" : "Salida"} registrada, ubicación solo aproximada `
                  + `(±${(geo.precision / 1000).toFixed(1)} km)`, "warn");
      else showToast(accion === "entrada" ? "Entrada registrada" : "Salida registrada");
      return r;
    } catch (e) {
      // El estado cambió por debajo (otra pestaña, o el autocierre de Odoo):
      // se recarga en vez de insistir.
      showToast(e.message || "No se pudo fichar", "error");
      await cargarEstado();
    } finally { setFichando(false); }
  };

  useEffect(() => { cargarEstado(); }, [cargarEstado]);

  // Al abrir ALM se contrastan los fichajes con Odoo y se traen los que aqui no
  // estan: no todo se ficha desde ALM —esta el boton del propio Odoo y las
  // correcciones que mete administracion— y el registro nuestro, que es el que
  // se conserva, salia con agujeros justo en los dias raros.
  //
  // Una vez por pestaña: recargar la pagina no tiene por que repetir la
  // consulta, y si algo entra en Odoo mientras tanto se recoge en la siguiente.
  useEffect(() => {
    if (!token || conectado !== true) return;
    try { if (sessionStorage.getItem(CLAVE_SINC)) return; } catch { /* */ }
    let vivo = true;
    (async () => {
      try {
        const r = await api.sincronizarFichajes();
        try { sessionStorage.setItem(CLAVE_SINC, "1"); } catch { /* */ }
        if (vivo && r.importados > 0) {
          showToast(`${r.importados} fichaje${r.importados === 1 ? "" : "s"} de Odoo `
                    + "que faltaban en ALM se han añadido al registro");
        }
      } catch (e) {
        // Un fallo de red o de sesion no debe molestar: se reintenta al abrir la
        // proxima vez. Pero si al servidor le falta la migracion, el registro no
        // se rellena NUNCA y el calendario de asistencias se queda vacio sin que
        // nadie se entere. Eso hay que decirlo.
        if (vivo && e?.code === "MIGRACION") {
          showToast("El servidor no puede contrastar los fichajes con Odoo: falta aplicar "
                    + "la migración 011 en la base de datos", "warn");
        }
      }
    })();
    return () => { vivo = false; };
  }, [token, conectado]); // eslint-disable-line

  // La ubicacion se pide por adelantado, no al pulsar: el GPS tarda demasiado
  // arrancando en frio y el fichaje se quedaba sin ella.
  useEffect(() => {
    if (!token || conectado !== true) return;
    precalentarUbicacion();
    const alVolver = () => { if (!document.hidden) precalentarUbicacion(); };
    document.addEventListener("visibilitychange", alVolver);
    return () => document.removeEventListener("visibilitychange", alVolver);
  }, [token, conectado]);

  return { estado, conectado, desde, hoy, horasHoy, cargando, fichando,
           cargarEstado, conectar, desconectar, fichar };
}
